import React, {
  useId,
} from "react";

import PropTypes from "prop-types";
import styled from "styled-components";

import {
  createAdminFormFieldDescriptor,
} from "./AdminFormFieldDescriptor";


/**
 * AdminFormField
 *
 * Generic presentation authority for a single Admin business
 * form field.
 *
 * Responsibilities:
 * - accessible label / description / error presentation
 * - generic control selection
 * - typed value normalization
 *
 * Explicitly excluded:
 * - business object mutation
 * - Retail knowledge
 * - persistence
 * - cross-field orchestration
 */
export default function AdminFormField({
  descriptor,
  value,
  onChange,
  error = null,
}) {
  const field =
    createAdminFormFieldDescriptor(
      descriptor
    );

  const generatedId = useId();

  const inputId =
    `admin-form-field-${field.name}-${generatedId}`;

  const descriptionId =
    field.description
      ? `${inputId}-description`
      : undefined;

  const errorId =
    error
      ? `${inputId}-error`
      : undefined;

  const describedBy = [
    descriptionId,
    errorId,
  ]
    .filter(Boolean)
    .join(" ")
    || undefined;


  const emit = (nextValue) => {
    if (
      typeof onChange
        === "function"
    ) {
      onChange(
        field.name,
        nextValue
      );
    }
  };


  const commonProps = {
    id: inputId,
    name: field.name,
    disabled: field.disabled,
    required: field.required,
    "aria-invalid":
      error
        ? "true"
        : undefined,
    "aria-describedby":
      describedBy,
  };


  let control = null;


  if (field.type === "textarea") {
    control = (
      <Textarea
        {...commonProps}
        value={
          value
            ?? ""
        }
        placeholder={
          field.placeholder
            ?? undefined
        }
        onChange={(event) =>
          emit(
            event.target.value
          )
        }
      />
    );
  } else if (
    field.type === "select"
  ) {
    control = (
      <Select
        {...commonProps}
        value={
          value
            ?? ""
        }
        onChange={(event) =>
          emit(
            event.target.value
          )
        }
      >
        {(field.options || [])
          .map((option) => {
            const optionValue =
              option.value;

            const optionLabel =
              option.label
              ?? String(
                optionValue
              );

            return (
              <option
                key={
                  String(
                    optionValue
                  )
                }
                value={
                  optionValue
                }
                disabled={
                  Boolean(
                    option.disabled
                  )
                }
              >
                {optionLabel}
              </option>
            );
          })}
      </Select>
    );
  } else if (
    field.type === "boolean"
  ) {
    control = (
      <BooleanRow>
        <Checkbox
          {...commonProps}
          type="checkbox"
          checked={
            Boolean(value)
          }
          onChange={(event) =>
            emit(
              event.target.checked
            )
          }
        />

        <BooleanLabel
          htmlFor={inputId}
        >
          {field.label}
        </BooleanLabel>
      </BooleanRow>
    );
  } else {
    control = (
      <Input
        {...commonProps}
        type={
          field.type
            === "number"
            ? "number"
            : "text"
        }
        value={
          value
            ?? ""
        }
        min={
          field.min
            ?? undefined
        }
        max={
          field.max
            ?? undefined
        }
        step={
          field.step
            ?? undefined
        }
        placeholder={
          field.placeholder
            ?? undefined
        }
        onChange={(event) => {
          if (
            field.type
              === "number"
          ) {
            const raw =
              event.target.value;

            emit(
              raw === ""
                ? ""
                : Number(raw)
            );

            return;
          }

          emit(
            event.target.value
          );
        }}
      />
    );
  }


  return (
    <FieldRoot
      data-admin-form-field={
        field.name
      }
      data-admin-form-field-type={
        field.type
      }
    >
      {field.type !== "boolean" ? (
        <Label
          htmlFor={inputId}
        >
          {field.label}

          {field.required ? (
            <RequiredMark
              aria-hidden="true"
            >
              *
            </RequiredMark>
          ) : null}
        </Label>
      ) : null}

      {control}

      {field.description ? (
        <Description
          id={descriptionId}
        >
          {field.description}
        </Description>
      ) : null}

      {error ? (
        <ErrorMessage
          id={errorId}
          role="alert"
        >
          {error}
        </ErrorMessage>
      ) : null}
    </FieldRoot>
  );
}


AdminFormField.propTypes = {
  descriptor:
    PropTypes.shape({
      name:
        PropTypes.string.isRequired,

      label:
        PropTypes.string.isRequired,

      type:
        PropTypes.oneOf([
          "text",
          "textarea",
          "number",
          "select",
          "boolean",
        ]).isRequired,

      required:
        PropTypes.bool,

      disabled:
        PropTypes.bool,

      placeholder:
        PropTypes.string,

      description:
        PropTypes.string,

      options:
        PropTypes.arrayOf(
          PropTypes.shape({
            value:
              PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
              ]).isRequired,

            label:
              PropTypes.node,

            disabled:
              PropTypes.bool,
          })
        ),

      min:
        PropTypes.number,

      max:
        PropTypes.number,

      step:
        PropTypes.number,

      validate:
        PropTypes.func,
    }).isRequired,

  value:
    PropTypes.any,

  onChange:
    PropTypes.func.isRequired,

  error:
    PropTypes.node,
};


const FieldRoot = styled.div`
  display: grid;
  gap: 0.45rem;
  min-width: 0;
`;


const Label = styled.label`
  color: #f7f7f7;
  font-size: 0.86rem;
  font-weight: 700;
`;


const BooleanLabel = styled.label`
  color: #f7f7f7;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
`;


const RequiredMark = styled.span`
  margin-left: 0.25rem;
  opacity: 0.72;
`;


const controlStyles = `
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  padding:
    0.7rem
    0.8rem;

  border:
    1px solid
    rgba(255, 255, 255, 0.12);

  border-radius:
    10px;

  background:
    rgba(7, 15, 35, 0.42);

  color:
    #f7f7f7;

  font: inherit;

  outline: none;

  transition:
    border-color 0.16s ease,
    background 0.16s ease;

  &:focus {
    border-color:
      rgba(96, 165, 250, 0.8);

    background:
      rgba(7, 15, 35, 0.62);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.58;
  }
`;


const Input = styled.input`
  ${controlStyles}
`;


const Textarea = styled.textarea`
  ${controlStyles}

  min-height: 7rem;
  resize: vertical;
`;


const Select = styled.select`
  ${controlStyles}
`;


const BooleanRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-height: 2.5rem;
`;


const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
  margin: 0;
`;


const Description = styled.small`
  color:
    rgba(247, 247, 247, 0.58);

  line-height: 1.45;
`;


const ErrorMessage = styled.div`
  color: #fecaca;
  font-size: 0.8rem;
  font-weight: 650;
`;
